import type { ReactNode } from "react";
import {
  CHILDREN_OPTIONS,
  GENDER_OPTIONS,
  HOUSE_OPTIONS,
  INCOME_OPTIONS,
  MARITAL_OPTIONS,
} from "../adminConfig";

export interface MemberFilters {
  name: string;
  phone: string;
  gender: string;
  city: string;
  maritalStatus: string;
  houseStatus: string;
  childrenStatus: string;
  income: string;
  ageMin: string;
  ageMax: string;
  heightMin: string;
  heightMax: string;
}

export const EMPTY_FILTERS: MemberFilters = {
  name: "",
  phone: "",
  gender: "",
  city: "",
  maritalStatus: "",
  houseStatus: "",
  childrenStatus: "",
  income: "",
  ageMin: "",
  ageMax: "",
  heightMin: "",
  heightMax: "",
};

interface FilterBarProps {
  filters: MemberFilters;
  onChange: (key: keyof MemberFilters, value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  isAdmin?: boolean;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="mb-1.5 block text-xs font-medium text-foreground-600">{label}</label>
      {children}
    </div>
  );
}

function OptionSelect({
  value,
  options,
  placeholder = "全部",
  onChange,
}: {
  value: string;
  options: string[];
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={[inputClass, value ? "" : "text-foreground-400"].join(" ")}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option} className="text-foreground-900">
          {option}
        </option>
      ))}
    </select>
  );
}

export default function FilterBar({
  filters,
  onChange,
  onSearch,
  onReset,
  loading = false,
  isAdmin = true,
}: FilterBarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center gap-2 border-b border-background-200 px-5 py-3.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
          <i className="ri-filter-3-line text-sm"></i>
        </span>
        <h2 className="font-heading text-sm font-semibold text-foreground-900">筛选条件</h2>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="姓名">
          <input
            type="text"
            value={filters.name}
            placeholder="输入姓名搜索"
            onChange={(e) => onChange("name", e.target.value)}
            className={inputClass}
          />
        </Field>

        {isAdmin && (
          <Field label="手机号">
            <input
              type="text"
              value={filters.phone}
              placeholder="输入手机号搜索"
              onChange={(e) => onChange("phone", e.target.value)}
              className={inputClass}
            />
          </Field>
        )}

        <Field label="性别">
          <OptionSelect
            value={filters.gender}
            options={GENDER_OPTIONS}
            onChange={(value) => onChange("gender", value)}
          />
        </Field>

        <Field label="现居城市">
          <input
            type="text"
            value={filters.city}
            placeholder="如：北京"
            onChange={(e) => onChange("city", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="婚姻状况">
          <OptionSelect
            value={filters.maritalStatus}
            options={MARITAL_OPTIONS}
            onChange={(value) => onChange("maritalStatus", value)}
          />
        </Field>

        <Field label="住房情况">
          <OptionSelect
            value={filters.houseStatus}
            options={HOUSE_OPTIONS}
            onChange={(value) => onChange("houseStatus", value)}
          />
        </Field>

        <Field label="年龄（起）">
          <input
            type="number"
            inputMode="numeric"
            value={filters.ageMin}
            placeholder="如：45"
            onChange={(e) => onChange("ageMin", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="年龄（止）">
          <input
            type="number"
            inputMode="numeric"
            value={filters.ageMax}
            placeholder="如：65"
            onChange={(e) => onChange("ageMax", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="身高（起）">
          <input
            type="number"
            inputMode="numeric"
            value={filters.heightMin}
            placeholder="如：160"
            onChange={(e) => onChange("heightMin", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="身高（止）">
          <input
            type="number"
            inputMode="numeric"
            value={filters.heightMax}
            placeholder="如：180"
            onChange={(e) => onChange("heightMax", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="月收入">
          <OptionSelect
            value={filters.income}
            options={INCOME_OPTIONS}
            onChange={(value) => onChange("income", value)}
          />
        </Field>

        <Field label="子女情况">
          <OptionSelect
            value={filters.childrenStatus}
            options={CHILDREN_OPTIONS}
            onChange={(value) => onChange("childrenStatus", value)}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3 border-t border-background-200 px-5 py-4 sm:flex-row">
        <button
          type="button"
          onClick={onSearch}
          disabled={loading}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-search-line"></i>}
          查询
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:border-primary-300"
        >
          <i className="ri-refresh-line"></i>
          重置
        </button>
      </div>
    </div>
  );
}